import React, { useState } from 'react';
import { Trash2, Plus, X, Star, Heart, Zap, Award, Target, TrendingUp, Users, Shield, Clock, Info, CheckCircle, Gift } from 'lucide-react';
import ColorSwatchPicker from './ColorSwatchPicker';

interface IconTextItem {
  id: string;
  icon: string;
  title: string;
  description: string;
}

interface IconTextBlockData {
  items: IconTextItem[];
  columns: 2 | 3 | 4;
  iconColor?: string;
}

const DEFAULT_ICON_COLOR = '#f97316';

interface IconTextBlockProps {
  data: IconTextBlockData;
  onChange: (data: IconTextBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const iconMap: { [key: string]: any } = {
  star: Star,
  heart: Heart,
  zap: Zap,
  shield: Shield,
  trending: TrendingUp,
  award: Award,
  check: CheckCircle,
  gift: Gift
};

const IconTextBlock: React.FC<IconTextBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  const addItem = () => {
    const newItem: IconTextItem = {
      id: Date.now().toString(),
      icon: 'star',
      title: 'หัวข้อใหม่',
      description: 'รายละเอียด...'
    };
    onChange({ ...data, items: [...data.items, newItem] });
  };

  const updateItem = (id: string, field: keyof IconTextItem, value: string) => {
    const updatedItems = data.items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    );
    onChange({ ...data, items: updatedItems });
  };

  const deleteItem = (id: string) => {
    onChange({ ...data, items: data.items.filter(item => item.id !== id) });
  };

  const getIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName] || Star;
    return IconComponent;
  };

  const gridColsClass = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  const iconColor = data.iconColor || DEFAULT_ICON_COLOR;

  if (isPreview) {
    return (
      <div className={`grid ${gridColsClass[data.columns]} gap-6`}>
        {data.items.map((item) => {
          const Icon = getIcon(item.icon);
          return (
            <div key={item.id} className="text-center p-6 bg-white rounded-xl border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: iconColor }}>
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600">{item.description}</p>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">ไอคอน + ข้อความ</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">จุดเด่นพร้อมไอคอน</p>
              <p className="text-gray-300">แสดงจุดเด่นหรือคุณสมบัติของสินค้าพร้อมไอคอนสวยงาม ช่วยให้ข้อมูลอ่านง่ายและน่าสนใจ</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">จำนวนคอลัมน์</label>
          <div className="flex items-center gap-2">
            <ColorSwatchPicker
              compact
              color={iconColor}
              fallback={DEFAULT_ICON_COLOR}
              onChange={(hex) => onChange({ ...data, iconColor: hex })}
            />
            <select
              value={data.columns}
              onChange={(e) => onChange({ ...data, columns: parseInt(e.target.value) as 2 | 3 | 4 })}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value={2}>2 คอลัมน์</option>
              <option value={3}>3 คอลัมน์</option>
              <option value={4}>4 คอลัมน์</option>
            </select>
          </div>
        </div>

        {data.items.map((item, index) => (
          <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-start mb-3">
              <span className="text-sm font-medium text-gray-700">รายการ #{index + 1}</span>
              <button
                onClick={() => deleteItem(item.id)}
                className="text-red-500 hover:text-red-700 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ไอคอน</label>
                <select
                  value={item.icon}
                  onChange={(e) => updateItem(item.id, 'icon', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                >
                  <option value="star">⭐ ดาว</option>
                  <option value="heart">❤️ หัวใจ</option>
                  <option value="zap">⚡ สายฟ้า</option>
                  <option value="shield">🛡️ โล่</option>
                  <option value="trending">📈 กราฟขึ้น</option>
                  <option value="award">🏆 รางวัล</option>
                  <option value="check">✅ ถูกต้อง</option>
                  <option value="gift">🎁 ของขวัญ</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">หัวข้อ</label>
                <input
                  type="text"
                  value={item.title}
                  onChange={(e) => updateItem(item.id, 'title', e.target.value)}
                  placeholder="หัวข้อ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">รายละเอียด</label>
                <textarea
                  value={item.description}
                  onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                  placeholder="รายละเอียด..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={addItem}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          เพิ่มรายการ
        </button>
      </div>
    </div>
  );
};

export default IconTextBlock;
